import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Brand } from "@/components/common/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Anchor, Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password, useRecovery ? null : mfaCode, useRecovery ? recoveryCode : null);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back to NavScora Admin");
      navigate("/");
    } else if (res.error === "MFA code required") {
      setMfaRequired(true);
      const attempted = useRecovery ? recoveryCode : mfaCode;
      setError(attempted ? (useRecovery ? "Invalid recovery code" : "Incorrect authenticator code") : "");
    } else {
      setError(res.error);
    }
  };

  const forgot = async () => {
    if (!email) return toast.error("Enter your email first");
    try { await api.post("/auth/forgot-password", { email: email.trim() }); } catch {}
    toast.success("If that email exists, a reset link has been sent.");
  };

  return (
    <div className="ns-grid-bg flex min-h-screen items-center justify-center bg-[#0B1120] px-4">
      <div className="ns-rise w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <Brand />
          <p className="mt-4 max-w-xs text-sm text-slate-500">
            NavScora Business Control Panel — secure internal access only.
          </p>
        </div>
        <div className="rounded-2xl border border-[#1E2D4A] bg-[#111A2E]/80 p-7 shadow-2xl backdrop-blur-md">
          <h1 className="text-xl font-bold text-slate-50">Sign in</h1>
          <p className="mt-1 text-sm text-slate-500">Enter your administrator credentials.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@navscora.com"
                className="border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500"
                data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className="border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500"
                data-testid="login-password-input" />
            </div>
            {mfaRequired && (
              <div className="space-y-2">
                {!useRecovery ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="mfa" className="text-slate-300">Authenticator code</Label>
                    <Input id="mfa" inputMode="numeric" maxLength={6} value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="6-digit code"
                      className="border-[#2D3F62] bg-[#0B1120] text-slate-100 tracking-widest focus-visible:ring-cyan-500"
                      data-testid="login-mfa-input" />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label htmlFor="recovery" className="text-slate-300">Recovery code</Label>
                    <Input id="recovery" value={recoveryCode}
                      onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                      placeholder="XXXX-XXXX-XXXX"
                      className="border-[#2D3F62] bg-[#0B1120] text-slate-100 tracking-wider focus-visible:ring-cyan-500"
                      data-testid="login-recovery-input" />
                  </div>
                )}
                <button type="button" onClick={() => { setUseRecovery((v) => !v); setError(""); }}
                  className="text-xs text-cyan-400 hover:text-cyan-300" data-testid="login-use-recovery-toggle">
                  {useRecovery ? "Use an authenticator code instead" : "Use a recovery code"}
                </button>
              </div>
            )}
            {error && <div role="alert" aria-live="assertive" className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500"
              data-testid="login-submit-button">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {mfaRequired ? "Verify & sign in" : "Sign in securely"}
            </Button>
            <button type="button" onClick={forgot} className="w-full text-center text-xs text-slate-400 hover:text-cyan-300" data-testid="forgot-password-link">
              Forgot password?
            </button>
          </form>
        </div>
        <div className="mt-5 flex items-center justify-center gap-2 text-[11px] text-slate-600">
          <ShieldCheck className="h-3.5 w-3.5" /> Encrypted session · MFA-ready · Access audited
        </div>
        <div className="mt-4 text-center text-[10px] text-slate-700" data-testid="login-footer">
          NavScora Admin™ is a trademark of Navscora Pty Ltd. All rights reserved.
        </div>
      </div>
    </div>
  );
}
